import {
  formatCounselPanelHtml,
  isCounselDraftPlaceholder,
  parseCounselPanelSections,
} from './formatCounselPanelHtml';

/** True when streamed counsel text has meaningful visible content for the panel. */
export function hasVisibleCounselStreamText(text: string | undefined | null): boolean {
  if (!text?.trim()) return false;
  const plain = text
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
  return plain.length >= 8;
}

function stripPartialCounselHtml(text: string): string {
  return text
    .replace(/<[^>]*$/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
}

/** True when the panel can actually render streamed counsel content (not just tag scaffolding). */
export function hasRenderableCounselStreamContent(text: string | undefined | null): boolean {
  if (!text?.trim()) return false;

  const looksHtml = /<[a-z][\s\S]*>/i.test(text);
  if (!looksHtml) {
    return hasVisibleCounselStreamText(text);
  }

  const partial = stripPartialCounselHtml(text);
  if (partial.length >= 8) return true;

  const formatted = formatCounselPanelHtml(text);
  if (!hasVisibleCounselStreamText(formatted)) return false;

  const sections = parseCounselPanelSections(formatted);
  return sections.some(
    (section) =>
      section.kind !== 'draft' ||
      (section.html.trim() && !isCounselDraftPlaceholder(section.html)),
  );
}

export type CounselActivityMode = 'ask' | 'draft' | 'review' | 'case' | 'workspace';

export function counselNodeStatusLabel(
  name: string,
  mode: CounselActivityMode = 'ask',
): string {
  switch (name) {
    case 'preparing':
      return 'Starting…';
    case 'gathering_context':
      if (mode === 'review') return 'Reading all document tabs and case file…';
      if (mode === 'draft') return 'Reading case file and templates…';
      return 'Reading your case and documents…';
    case 'web_search':
      return 'Searching online for supplementary legal sources…';
    case 'answering':
      if (mode === 'review') return 'Reviewing for filing readiness…';
      return 'Composing response…';
    case 'drafting':
      if (mode === 'review') return 'Preparing review findings…';
      if (mode === 'draft') return 'Writing draft…';
      return 'Composing response…';
    case 'writing_document':
      return 'Writing into your document…';
    case 'finalizing':
      if (mode === 'draft') return 'Applying formatting…';
      return 'Finishing up…';
    default:
      return 'Working…';
  }
}

export function counselStreamIntoDocumentPanelNote(): string {
  return 'Draft is appearing in the editor below.';
}
